import React, { useState } from "react";
import Client from "../../models/client";
import useSWR from "swr";
import { deleteClientUser, fetchCurrentUser, fetchUserList } from "../api";
import { Box, Button, Center, Group, Loader, ThemeIcon, Title } from "@mantine/core";
import { ADMIN_TYPE, AdminUser } from "../../models/user";
import { notifications } from "@mantine/notifications";
import { PlusIcon, UserCircleIcon } from "@phosphor-icons/react";
import { UsersTable } from "../components/UsersTable";
import { UserCreateModal } from "../components/UserCreateModal";
import { UserEmailUpdateModal } from "../components/UserEmailUpdateModal";
import { UserPasswordUpdateModal } from "../components/UserPasswordUpdateModal";
import { UserAdminTypeUpdateModal } from "../components/UserAdminTypeUpdateModal";
import { confirmDelete } from "../utils/confirm";

interface Props {
  client: Client
}

type ModalKind = 'create' | 'email' | 'password' | 'admin_type' | null

const UsersContainer: React.FC<Props> = ({ client }) => {
  const { data: users, mutate: mutateUsers } = useSWR(
    `/admin/clients/${client.id}/users`,
    () => fetchUserList(client.id),
  )

  const { data: currentUser } = useSWR(
    `/admin/clients/${client.id}/users/current`,
    () => fetchCurrentUser(client.id),
  )

  const [openedModal, setOpenedModal] = useState<ModalKind>(null)
  const [targetUser, setTargetUser] = useState<AdminUser | null>(null)

  if (!users || !currentUser) {
    return (
      <Center mih={400}>
        <Loader />
      </Center>
    )
  }

  const handleClickCreate = () => setOpenedModal('create')
  const handleEditEmail = (user: AdminUser) => { setTargetUser(user); setOpenedModal('email') }
  const handleEditPassword = (user: AdminUser) => { setTargetUser(user); setOpenedModal('password') }
  const handleEditAdminType = (user: AdminUser) => { setTargetUser(user); setOpenedModal('admin_type') }
  const handleCloseModal = () => setOpenedModal(null)

  const handleDelete = async (user: AdminUser) => {
    if (!confirmDelete('ID')) return

    try {
      await deleteClientUser(client.id, user.id)
      await mutateUsers()
      notifications.show({ message: 'IDを削除しました', color: 'teal' })
    } catch (error: any) {
      console.error('IDの削除に失敗しました。', error)
      notifications.show({ message: 'IDの削除に失敗しました', color: 'red' })
    }
  }

  const canCreate = currentUser.admin_type === ADMIN_TYPE.ADMIN && users.length < client.account_limit

  return (
    <Box>
      <Group gap={12} mb={16}>
        <ThemeIcon variant="transparent" color="#CA8E00">
          <UserCircleIcon weight="fill" size={36} />
        </ThemeIcon>
        <Title size={24} fw={600} c="#000000CC">ID一覧</Title>
      </Group>

      {canCreate && (
        <Group justify="end" mb={16}>
          <Button
            variant="filled"
            radius="xl"
            fz={13}
            fw={600}
            onClick={handleClickCreate}
            leftSection={<PlusIcon size={16} weight="bold" />}
          >
            新規作成
          </Button>
        </Group>
      )}

      <UsersTable
        users={users}
        currentUser={currentUser}
        onEditEmail={handleEditEmail}
        onEditPassword={handleEditPassword}
        onEditAdminType={handleEditAdminType}
        onDelete={handleDelete}
      />

      <UserCreateModal
        opened={openedModal === 'create'}
        onClose={handleCloseModal}
        clientId={client.id}
        onSaved={mutateUsers}
      />

      {targetUser && (
        <>
          <UserEmailUpdateModal
            opened={openedModal === 'email'}
            onClose={handleCloseModal}
            clientId={client.id}
            user={targetUser}
            onSaved={mutateUsers}
          />
          <UserPasswordUpdateModal
            opened={openedModal === 'password'}
            onClose={handleCloseModal}
            clientId={client.id}
            user={targetUser}
            isSelf={targetUser.id === currentUser.id}
            onSaved={mutateUsers}
          />
          <UserAdminTypeUpdateModal
            opened={openedModal === 'admin_type'}
            onClose={handleCloseModal}
            clientId={client.id}
            user={targetUser}
            onSaved={mutateUsers}
          />
        </>
      )}
    </Box>
  )
}

export default UsersContainer
